import { isNodeSelection, useCurrentEditor } from '@tiptap/react';
import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus';
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';

export interface EditorBubbleProps extends Omit<BubbleMenuProps, 'editor'> {
	readonly children: ReactNode;
}

export const EditorBubble = forwardRef<HTMLDivElement, EditorBubbleProps>(
	({ children, shouldShow: customShouldShow, options, ...rest }, ref) => {
		const { editor } = useCurrentEditor();

		const shouldShow = useMemo<BubbleMenuProps['shouldShow']>(() => {
			return (props) => {
				const { editor, state } = props;
				const { selection } = state;

				if (
					!editor?.isEditable ||
					editor.isActive('image') ||
					selection.empty ||
					isNodeSelection(selection)
				) {
					return false;
				}

				if (customShouldShow) {
					return customShouldShow(props);
				}

				return true;
			};
		}, [customShouldShow]);

		if (!editor) return null;

		return (
			<BubbleMenu
				ref={ref}
				editor={editor}
				shouldShow={shouldShow}
				options={{
					placement: 'top',
					strategy: 'fixed',
					...options,
				}}
				{...rest}
			>
				{children}
			</BubbleMenu>
		);
	},
);

EditorBubble.displayName = 'EditorBubble';

export default EditorBubble;
